import React from 'react';
import { makeStyles, Tooltip } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import { FunctionField, Record, TextField, useTranslate } from 'react-admin';
import { useDispatch } from 'react-redux';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { selectAnc } from '../../redux/actions/ancs';
import { tableStyles } from '../../theme/customTheme';
import { toFormatDate } from '../../utils/date';
import { getNameWithoutPrefix } from '../../utils/resources/MetadataAvatarType';
import { MAINTENANCE, SLUDGE_EMPTYING } from '../../utils/rikutec';
import { SourceBoardV2 } from '../../utils/types';
import useAvatarAppLinks from '../avatarAnc/hooks/useAvatarAppLinks';
import { EnabledColumns, hasRoles } from '../avatarAnc/TableView1';
import CreationDateField from '../avatarAnc/columns/CreationDateField';
import DataGridCustom from '../UI/DataGrid';
import LastNoteField from './LastNoteField';

//@ts-ignore
const useStyles = makeStyles(() => ({ ...tableStyles() }));

const orNA = (value: string | number | null | undefined) => value || 'N/A';

/**
 * The v2 board as a table: the columns of the v1 table plus those of the forms. Only the columns the
 * API can order by are sortable (see rikutec_source_board_v2.filter.order in statisticsservice).
 */
const BoardV2Table = ({ enabledColumns }: { enabledColumns: EnabledColumns }) => {
    const t = useTranslate();
    const classes: any = useStyles();
    const dispatch = useDispatch();
    const { permissions } = useRolePermissions();
    const { simulateFingerMc, openBackofficeForms } = useAvatarAppLinks();

    const dataGridProps = {
        rowClick: (id: string, basePath: string, record: Record) => {
            dispatch(selectAnc(record));
            return null;
        },
        toFindSelected: { resource: 'ancs', key: 'selected' },
        hasBulkActions: null,
        classes: {
            checkbox: classes.checkbox,
            row: classes.list,
        },
    };

    return (
        <DataGridCustom {...dataGridProps}>
            <TextField label={t('app.ancs.productTypology')} source='productTypology' />
            <TextField label={t('app.ancs.articleNumber')} source='productCode' />
            <FunctionField
                label={t('app.ancs.metadataAvatarType.name')}
                source='productTypeName'
                // @ts-ignore
                render={(record: SourceBoardV2) => <span>{getNameWithoutPrefix(record.productTypeName)}</span>}
            />
            <TextField label={t('app.ancs.serialNumber')} source='productSerialNumber' />
            <TextField label={t('app.ancs.postalCode')} source='productPostalCode' />
            <FunctionField
                label={t('app.objects.info.created')}
                source='creationDate'
                // @ts-ignore
                render={(record: SourceBoardV2) => <CreationDateField record={record} />}
            />
            <FunctionField
                label={t('app.ancsV2.columns.clientFullName')}
                source='clientFullName'
                // @ts-ignore
                render={(record: SourceBoardV2) => orNA(record.clientFullName)}
            />
            <FunctionField
                label={t('app.ancsV2.columns.installerCompany')}
                source='installerCompany'
                // @ts-ignore
                render={(record: SourceBoardV2) => orNA(record.installerCompany)}
            />
            <FunctionField
                label={t('app.ancsV2.columns.installationCity')}
                source='installationCity'
                // @ts-ignore
                render={(record: SourceBoardV2) => orNA(record.installationCity)}
            />
            <FunctionField
                label={t('app.ancsV2.columns.poseDate')}
                source='poseDate'
                // @ts-ignore
                render={(record: SourceBoardV2) => (record.poseDate ? toFormatDate(record.poseDate) : 'N/A')}
            />
            <FunctionField
                label={t('app.ancsV2.columns.lastMaintenance')}
                source='productLastMaintenanceDate'
                sortByOrder='DESC'
                // @ts-ignore
                render={(record: SourceBoardV2) => (
                    <LastNoteField
                        date={record.productLastMaintenanceDate}
                        note={record.productLastMaintenanceData}
                        tag={MAINTENANCE}
                    />
                )}
            />
            <FunctionField
                label={t('app.ancsV2.columns.lastEmptying')}
                source='productLastEmptyingDate'
                sortByOrder='DESC'
                // @ts-ignore
                render={(record: SourceBoardV2) => (
                    <LastNoteField
                        date={record.productLastEmptyingDate}
                        note={record.productLastEmptyingData}
                        tag={SLUDGE_EMPTYING}
                    />
                )}
            />
            {enabledColumns.currentState && (
                <FunctionField
                    source='productCurrentStatusName'
                    label={t('app.ancs.nodeCurrentState')}
                    // @ts-ignore
                    render={(record: SourceBoardV2) => record.productCurrentStatusName?.toUpperCase()}
                />
            )}
            {enabledColumns.currentState && (
                <FunctionField
                    source='productCurrentStatusDate'
                    label={t('app.ancs.nodeDateOfTakesPlace')}
                    // @ts-ignore
                    render={(record: SourceBoardV2) =>
                        record.productCurrentStatusDate ? toFormatDate(record.productCurrentStatusDate) : 'N/A'
                    }
                />
            )}
            <FunctionField
                label={t('app.ancsV2.groups.signatures')}
                sortable={false}
                // @ts-ignore
                render={(record: SourceBoardV2) =>
                    `${[record.hasOwnerSignature, record.hasInstallerSignature].filter(Boolean).length}/2`
                }
            />
            {enabledColumns.processingCapacity && (
                <TextField source='processingCapacity' label={t('app.ancs.processingCapacity')} />
            )}
            {enabledColumns.sludgeStorageVolume && (
                <TextField source='sludgeStorageVolume' label={t('app.ancs.sludgeStorageVolume')} />
            )}
            {enabledColumns.totalUsefulVolume && (
                <TextField source='totalUsefulVolume' label={t('app.ancs.totalUsefulVolume')} />
            )}
            {enabledColumns.model && <TextField source='model' label={t('app.ancs.model')} />}
            <FunctionField
                label={t('app.ancs.accessToFinger')}
                sortable={false}
                // @ts-ignore
                render={(record: SourceBoardV2) => (
                    <IconButton
                        aria-label={t('app.ancs.accessToFinger')}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (record.avatarId) simulateFingerMc(record.avatarId);
                        }}
                    >
                        <Tooltip title={t('app.ancs.accessToFinger')}>
                            <PhoneAndroidIcon />
                        </Tooltip>
                    </IconButton>
                )}
            />
            {hasRoles(['ROLE_RKT_PROCESSUS_SPECIFIQUE_WEBAPP_DASHBORD'], permissions) && (
                <FunctionField
                    label={t('app.ancs.vueSpecifique')}
                    sortable={false}
                    // @ts-ignore
                    render={(record: SourceBoardV2) => (
                        <IconButton
                            aria-label={t('app.ancs.vueSpecifique')}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (record.avatarId) openBackofficeForms(record.avatarId);
                            }}
                        >
                            <Tooltip title={t('app.ancs.vueSpecifique')}>
                                <AssignmentIcon />
                            </Tooltip>
                        </IconButton>
                    )}
                />
            )}
        </DataGridCustom>
    );
};

export default BoardV2Table;
